import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch, apiUpload } from '@/lib/api/rawFetch'

// Interfaces declared locally until the next `npm run gen-api` refresh
// (same escape hatch as useAdmin.ts) — mirror backend/schemas/events.py.
export type Discipline = 'hunter_jumper' | 'dressage' | 'eventing'

export interface EventLocation {
  location_id: number
  name: string
}

export interface EventResponse {
  id: number
  shopify_location_id: number
  location_name: string | null
  event_date: string
  event_duration_days: number
  discipline: string
  booth_fee: number
  labor_cost: number
  travel_lodging: number
  offline_revenue: number
  offline_cost: number
  synced_at: string | null
  products_synced: number
  sync_error: string | null
  debrief_completed_at: string | null
  pos_revenue: number
  units_carryover: number
  units_transferred: number
  units_sold: number
  overall_sell_through_pct: number | null
  created_at: string | null
  updated_at: string | null
}

export interface EventCreate {
  shopify_location_id: number
  event_date: string
  event_duration_days: number
  discipline: Discipline
  booth_fee?: number
  labor_cost?: number
  travel_lodging?: number
}

export type EventUpdate = Partial<EventCreate>

export interface EventImportRowResult {
  row_index: number
  status: 'created' | 'skipped_duplicate' | 'error'
  location_name: string
  event_date: string
  message: string
}

export interface EventImportResponse {
  created: number
  skipped_duplicates: number
  errors: number
  rows: EventImportRowResult[]
}

export function useEventLocations() {
  return useQuery({
    queryKey: ['events', 'locations'] as const,
    queryFn: () => apiFetch<EventLocation[]>('GET', '/api/events/locations'),
    staleTime: 5 * 60_000,
  })
}

export function useEvents(filters?: { location_id?: number; discipline?: string; year?: number }) {
  return useQuery({
    queryKey: ['events', 'list', filters ?? {}] as const,
    queryFn: () =>
      apiFetch<EventResponse[]>('GET', '/api/events/', {
        query: {
          location_id: filters?.location_id,
          discipline: filters?.discipline,
          year: filters?.year,
        },
      }),
    staleTime: 30_000,
  })
}

export function useCreateEvent() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: EventCreate) =>
      apiFetch<EventResponse>('POST', '/api/events/', { body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['events'] }),
  })
}

export function useUpdateEvent() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ eventId, body }: { eventId: number; body: EventUpdate }) =>
      apiFetch<EventResponse>('PUT', `/api/events/${eventId}`, { body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['events'] }),
  })
}

export function useDeleteEvent() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (eventId: number) => apiFetch('DELETE', `/api/events/${eventId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['events'] }),
  })
}

export function useImportSchedule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (file: File) => apiUpload<EventImportResponse>('/api/events/import', file),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['events'] }),
  })
}

// --- Phase 1: sync, analysis, size curve, trend, live pace ---

export interface AnalysisVariant {
  variant_id: number
  product_id: number | null
  sku: string | null
  product_title: string | null
  variant_title: string | null
  product_type: string | null
  vendor: string | null
  size: string | null
  discipline_tag: string | null
  qty_carryover: number
  qty_transferred: number
  qty_available: number
  qty_sold: number
  qty_unsold: number
  sell_through_pct: number | null
  revenue: number
  /** Offline sales (outside POS) included in `revenue`; absent on channel analysis. */
  offline_revenue?: number
  /** Offline units — not in qty_sold / sell-through, which stay POS-only. */
  offline_qty?: number
  unit_cost: number | null
  gross_margin: number | null
  margin_pct: number | null
  stockout_flag: boolean
  stockout_at: string | null
  est_missed_units: number | null
  est_missed_revenue: number | null
}

export interface AnalysisProduct {
  product_id: number | null
  product_title: string
  vendor: string | null
  qty_carryover: number
  qty_transferred: number
  qty_available: number
  qty_sold: number
  revenue: number
  /** Offline sales (outside POS) included in `revenue`; absent on channel analysis. */
  offline_revenue?: number
  /** Offline units — not in qty_sold / sell-through, which stay POS-only. */
  offline_qty?: number
  sell_through_pct: number | null
  /** Group margin over the sold rows with a known unit cost; null when none. */
  margin_pct: number | null
  stockout_count: number
  variants: AnalysisVariant[]
}

export interface AnalysisCategory {
  product_type: string
  qty_carryover: number
  qty_transferred: number
  qty_available: number
  qty_sold: number
  revenue: number
  /** Offline sales (outside POS) included in `revenue`; absent on channel analysis. */
  offline_revenue?: number
  /** Offline units — not in qty_sold / sell-through, which stay POS-only. */
  offline_qty?: number
  sell_through_pct: number | null
  margin_pct: number | null
  stockout_count: number
  products: AnalysisProduct[]
}

export interface EventAnalysis {
  totals: {
    qty_carryover: number
    qty_transferred: number
    qty_available: number
    qty_sold: number
    revenue: number
    offline_revenue?: number
    offline_qty?: number
    sell_through_pct: number | null
    margin_pct: number | null
  }
  categories: AnalysisCategory[]
  event: EventResponse
  event_ids: number[]
  facets: { product_types: string[]; vendors: string[] }
}

export interface SizeCurveEntry {
  size: string
  qty_transferred: number
  qty_available: number
  qty_sold: number
  sell_through_pct: number | null
  stockout_count: number
  event_count: number
}

export interface WeeklyTrendEntry {
  week_number: number
  event_id: number
  event_date: string
  discipline: string | null
  revenue: number
  sell_through_pct: number | null
}

export interface LivePaceProduct {
  variant_id: number
  sku: string | null
  product_title: string | null
  variant_title: string | null
  size: string | null
  qty_transferred: number
  qty_available: number
  qty_sold_so_far: number
  hours_elapsed: number
  sales_pace_per_hour: number
  projected_sell_through_pct: number | null
  stockout_risk: boolean
  sold_out: boolean
  slow_mover: boolean
}

export interface LivePaceResponse {
  event_id: number
  as_of: string
  products: LivePaceProduct[]
}

export function useSyncEvent() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (eventId: number) =>
      apiFetch<{ products_synced: number; transfers_found: number; orders_found: number; warnings: string[] }>(
        'POST', `/api/events/${eventId}/sync`
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['events'] }),
  })
}

export function useEventAnalysis(
  eventIds: number[],
  filters?: { product_type?: string; vendor?: string; discipline?: string }
) {
  // First id is the primary event; the rest merge into a combined
  // multi-week view (same location, per-variant sums, earliest-week
  // carryover — see backend merge_event_rows).
  const [primary, ...extras] = eventIds
  return useQuery({
    queryKey: ['events', 'analysis', eventIds, filters ?? {}] as const,
    queryFn: () =>
      apiFetch<EventAnalysis>('GET', `/api/events/${primary}/analysis`, {
        query: {
          product_type: filters?.product_type,
          vendor: filters?.vendor,
          discipline: filters?.discipline,
          extra_event_ids: extras.length > 0 ? extras.join(',') : undefined,
        },
      }),
    enabled: primary != null,
    staleTime: 60_000,
  })
}

// --- Non-event sales channels (Brewster Store / Online) ---
// Same drill-down shape as event analysis so the Analysis page can render
// either from one code path. A channel has no pack list, so the transfer
// fields carry stock-pool semantics instead: qty_carryover = on hand at the
// store when the period opened, qty_transferred = units restocked during it
// (see backend channel analysis).
// 'all' pools every location and channel — sales only, no stock denominator.
export type EventChannel = 'brewster' | 'online' | 'all'

/** 'YYYY-MM-DD' Monday for a week period, 'YYYY-MM' for a calendar month,
 *  'YYYY-MM-DD' for a single day (the date presets resolve to day runs). */
export type ChannelPeriodType = 'week' | 'month' | 'day'

export interface ChannelTrendPoint {
  period_start: string
  revenue: number
  units_sold: number
}

// How much stock history backs the requested span. Only Brewster-location
// snapshots count — stock left at an event venue can't be sold at the store or
// online. Uncovered spans (before snapshots existed) can still show sales —
// Available/sell-through/missed are the fields that go blank.
export interface ChannelStock {
  covered: boolean
  start_date: string | null
  source: 'brewster' | null
  /** First day the store's stock history has data; null until it exists. */
  history_from: string | null
  /** Whole store pool at period start — the shelf-turn denominator. 0 when uncovered or channel 'all'. */
  pool_units: number
  /** How many variants that pool spans (sold and unsold alike). 0 when uncovered or channel 'all'. */
  pool_variants: number
}

export interface ChannelAnalysis {
  channel: EventChannel
  channel_label: string
  period_type: ChannelPeriodType
  periods: string[]
  totals: EventAnalysis['totals']
  categories: AnalysisCategory[]
  facets: { product_types: string[]; vendors: string[] }
  trend: ChannelTrendPoint[]
  stock: ChannelStock
  /** Sales with no store stock behind them (dropship, partner locations, event stock); null when stock is uncovered or channel is 'all'. */
  sold_without_stock: { units: number; revenue: number } | null
}

export function useChannelAnalysis(
  channel: EventChannel | null,
  periods: string[],
  filters?: { product_type?: string; vendor?: string; discipline?: string },
  kind?: ChannelPeriodType
) {
  // Periods are all one type — the picker never mixes them. 'YYYY-MM' is 7
  // chars and 'YYYY-MM-DD' is 10, so weeks and months tell themselves apart;
  // days look exactly like week starts, so that case has to say so via `kind`.
  const periodType: ChannelPeriodType = kind ?? (periods[0]?.length === 7 ? 'month' : 'week')
  // Period order doesn't change the result — sort in the key so re-picking the
  // same set in a different order is a cache hit. The type rides in the key
  // too: the same date list means something different as a day than a week.
  const keyPeriods = [...periods].sort()
  // Days go over as ONE inclusive range rather than 30 comma-separated dates.
  const dayRange =
    periodType === 'day' && keyPeriods.length > 0
      ? `${keyPeriods[0]}:${keyPeriods[keyPeriods.length - 1]}`
      : undefined
  return useQuery({
    queryKey: ['events', 'channel-analysis', channel, periodType, keyPeriods, filters ?? {}] as const,
    queryFn: () =>
      apiFetch<ChannelAnalysis>('GET', `/api/events/channels/${channel}/analysis`, {
        query: {
          weeks: periodType === 'week' ? periods.join(',') : undefined,
          months: periodType === 'month' ? periods.join(',') : undefined,
          days: dayRange,
          product_type: filters?.product_type,
          vendor: filters?.vendor,
          discipline: filters?.discipline,
        },
      }),
    enabled: channel != null && periods.length > 0,
    // The backend persists these Shopify pulls; don't re-hit it on every tab
    // refocus.
    staleTime: 5 * 60_000,
  })
}

export function useSizeCurve(eventId: number | undefined, productId: number | null) {
  return useQuery({
    queryKey: ['events', 'size-curve', eventId, productId] as const,
    queryFn: () =>
      apiFetch<{ product_id: number; sizes: SizeCurveEntry[] }>(
        'GET', `/api/events/${eventId}/size-curve`, { query: { product_id: productId } }
      ),
    enabled: eventId != null && productId != null,
    staleTime: 60_000,
  })
}

export function useWeeklyTrend(
  locationId: number | undefined,
  filters?: { discipline?: string; year?: number }
) {
  return useQuery({
    queryKey: ['events', 'weekly-trend', locationId, filters ?? {}] as const,
    queryFn: () =>
      apiFetch<{ weeks: WeeklyTrendEntry[] }>(
        'GET', `/api/events/locations/${locationId}/weekly-trend`,
        { query: { discipline: filters?.discipline, year: filters?.year } }
      ),
    enabled: locationId != null,
    staleTime: 60_000,
  })
}

export function useLivePace(eventId: number | undefined) {
  return useQuery({
    queryKey: ['events', 'live', eventId] as const,
    queryFn: () => apiFetch<LivePaceResponse>('GET', `/api/events/${eventId}/live`),
    enabled: eventId != null,
    refetchInterval: 300_000, // spec: poll every 5 minutes during active events
    refetchIntervalInBackground: false,
    retry: false, // 409 when the event isn't active — don't hammer
  })
}

// --- Phase 2: pack list recommendations, debrief, intelligence ---

export interface RecommendationSizeCurveEntry {
  size: string
  sell_through_pct: number | null
  qty_transferred: number
  qty_available: number
  qty_sold: number
}

export interface RecommendationItem {
  variant_id: number
  product_id: number | null
  sku: string | null
  product_title: string | null
  variant_title: string | null
  size: string | null
  product_type: string | null
  vendor: string | null
  discipline_tag: string | null
  status: 'HISTORY' | 'NEW' | 'CROSS_DISCIPLINE'
  last_event_date: string | null
  last_event_discipline: string | null
  last_qty_transferred: number | null
  last_qty_available: number | null
  last_qty_sold: number | null
  last_sell_through_pct: number | null
  last_stockout: boolean
  margin_pct: number | null
  recommended_qty: number
  rec_reason: string
  /** Hand-typed offline units counted one-for-one in recommended_qty (0 = none). */
  offline_qty: number
  /** Units estimated to already be at the venue — already subtracted from recommended_qty. */
  est_at_venue: number
  brewster_stock: number
  stock_ok: boolean
  online_weekly_velocity: number
  remaining_after_pull: number
  online_risk: boolean
  size_curve: RecommendationSizeCurveEntry[]
}

export interface RecommendationsResponse {
  generated_at: string
  items: RecommendationItem[]
  /** The past event the at-venue estimate is based on. */
  at_venue_event_date?: string | null
}

export function useRecommendations(
  locationId: number | undefined,
  filters: { discipline?: string; event_date?: string }
) {
  return useQuery({
    queryKey: ['events', 'recommendations', locationId, filters] as const,
    queryFn: () =>
      apiFetch<RecommendationsResponse>(
        'GET', `/api/events/locations/${locationId}/recommendations`,
        { query: { discipline: filters.discipline, event_date: filters.event_date } }
      ),
    enabled: locationId != null && !!filters.discipline && !!filters.event_date,
    staleTime: 60_000,
  })
}

export interface DebriefUpdate {
  rating?: number
  weather?: string[]
  attendance?: number
  competition?: string
  worked?: string
  didnt_work?: string
  attend_again?: 'yes' | 'no' | 'maybe'
}

export function useSaveDebrief() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ eventId, body }: { eventId: number; body: DebriefUpdate }) =>
      apiFetch<EventResponse>('PATCH', `/api/events/${eventId}/debrief`, { body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['events'] }),
  })
}

// --- Offline sales: per-event manual entries for sales outside POS (e.g.
// Shopify draft orders placed at a show). EventResponse.offline_revenue /
// offline_cost are the server-side sums of these entries.

/** product = a catalog variant; custom = not in the catalog, typed only;
 *  unclassified = entered before products were tracked — needs editing. */
export type OfflineSaleKind = 'product' | 'custom' | 'unclassified'

export interface OfflineSale {
  id: number
  event_id: number
  amount: number
  cost: number
  sale_date: string
  note: string
  kind: OfflineSaleKind
  qty: number | null
  variant_id: number | null
  product_id: number | null
  product_title: string | null
  variant_title: string | null
  sku: string | null
  vendor: string | null
  product_type: string | null
  created_by: string
  created_at: string
  updated_by: string | null
  updated_at: string | null
}

// Exactly one of variant_id / product_type on create; the server fills the
// titles, SKU, vendor and type from the variant, so they're never sent.
interface OfflineSaleFields {
  amount: number
  cost: number
  sale_date: string
  note: string
  qty: number
}

export type OfflineSaleCreate =
  | (OfflineSaleFields & { variant_id: number; product_type?: never })
  | (OfflineSaleFields & { product_type: string; variant_id?: never })

// Any subset; at most one of variant_id / product_type (switching clears the
// other side). Neither = leave the classification as it is.
export type OfflineSaleUpdate = Partial<OfflineSaleFields> &
  ({ variant_id?: number; product_type?: never } | { product_type?: string; variant_id?: never })

export function useOfflineSales(eventId: number | undefined) {
  return useQuery({
    queryKey: ['events', 'offline-sales', eventId] as const,
    queryFn: () => apiFetch<OfflineSale[]>('GET', `/api/events/${eventId}/offline-sales`),
    enabled: eventId != null,
    staleTime: 60_000,
  })
}

/** A catalog variant's current Shopify selling price (not compare-at), in
 *  dollars — 0 when the mirror has none. One-shot lookup after a pick. */
export function fetchVariantPrice(variantId: number) {
  return apiFetch<{ variant_id: number; price: number }>(
    'GET',
    `/api/events/variants/${variantId}/price`
  )
}

// Every key that carries event revenue (list, analysis, weekly trend,
// profitability) — and the offline-sales list itself — lives under
// ['events'], so one prefix invalidation keeps them all in step.
export function useAddOfflineSale() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ eventId, body }: { eventId: number; body: OfflineSaleCreate }) =>
      apiFetch<OfflineSale>('POST', `/api/events/${eventId}/offline-sales`, { body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['events'] }),
  })
}

export function useUpdateOfflineSale() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ eventId, saleId, body }: { eventId: number; saleId: number; body: OfflineSaleUpdate }) =>
      apiFetch<OfflineSale>('PATCH', `/api/events/${eventId}/offline-sales/${saleId}`, { body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['events'] }),
  })
}

export function useDeleteOfflineSale() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ eventId, saleId }: { eventId: number; saleId: number }) =>
      apiFetch('DELETE', `/api/events/${eventId}/offline-sales/${saleId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['events'] }),
  })
}

export interface ProfitabilityRow {
  event_id: number
  location_name: string
  event_date: string
  discipline: string
  pos_revenue: number
  pos_cogs: number
  pos_gross_margin: number
  offline_revenue: number
  offline_cost: number
  total_revenue: number
  total_gross_margin: number
  booth_fee: number
  labor_cost: number
  travel_lodging: number
  total_event_cost: number
  net_profit: number | null
  profit_margin_pct: number | null
  margin_per_cost_dollar: number | null
  missing_cost_count: number
  synced: boolean
}

export function useProfitability() {
  return useQuery({
    queryKey: ['events', 'intelligence', 'profitability'] as const,
    queryFn: () =>
      apiFetch<{ events: ProfitabilityRow[] }>('GET', '/api/events/intelligence/profitability'),
    staleTime: 60_000,
  })
}

export interface MissingCostVariant {
  variant_id: number
  sku: string | null
  product_title: string | null
  variant_title: string | null
  vendor: string | null
  event_count: number
  total_qty_sold: number
}

export function useMissingCosts() {
  return useQuery({
    queryKey: ['events', 'intelligence', 'missing-costs'] as const,
    queryFn: () =>
      apiFetch<{ variants: MissingCostVariant[] }>('GET', '/api/events/intelligence/missing-costs'),
    staleTime: 60_000,
  })
}
